import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { format } from 'date-fns';
import { PenIcon, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import useTestimonial from '../../stores/useTestimonial';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import Skeleton from '../../components/ui/skeleton';

interface TestimoniCardProps {
  testimoni_id: number;
  name_testi: string;
  message_testi_id: string;
  photo_testi?: string;
  date?: Date;
  onEdit: () => void;
  onDelete: () => void;
}

function TestimoniCard({
  testimoni_id,
  name_testi,
  message_testi_id,
  photo_testi,
  date,
  onEdit,
  onDelete,
}: TestimoniCardProps) {
  const formattedDate = date ? format(new Date(date), 'd MMM yyyy') : 'Not now';
  return (
    <div className="h-fit hover-shadow bg-neutral-50 flex flex-col gap-4 p-4 border border-border rounded-lg overflow-hidden">
      <p className="text-sm">“{message_testi_id}“</p>
      <div className="flex justify-between items-center">
        <div className="flex gap-2">
          <img src={photo_testi} className="w-12 object-cover rounded-full h-12" alt="" />
          <div>
            <h2 className="font-medium whitespace-nowrap overflow-hidden text-ellipsis">{name_testi}</h2>
            <p className="text-sm text-secondNeutral">{formattedDate}</p>
          </div>
        </div>
        <div className="flex gap-2 px-2 py-1.5 items-center border border-border rounded-lg">
          <button type="button" onClick={onDelete}>
            <Trash2 className="h-4 text-red-600" />
            <span className="sr-only">Delete</span>
          </button>
          <button type="button" onClick={onEdit}>
            <PenIcon className="h-4 text-primary" />
            <span className="sr-only">Edit</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ListTesti() {
  const { testimonials, removeLoading, fetchLoading, hasFetched, fetchTestimonials, removeTestimonial } =
    useTestimonial();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTestimonial, setSelectedTestimonial] = useState<{ id: number; name: string } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [visibleTestimonials, setVisibleTestimonials] = useState(testimonials.slice(0, 5));

  useEffect(() => {
    if (!hasFetched) {
      fetchTestimonials();
    }
  }, [fetchTestimonials, hasFetched]);

  useEffect(() => {
    if (testimonials.length > 0) {
      const sortedTestimonials = [...testimonials].sort(
        (a, b) => new Date(b.edited_at ?? new Date(0)).getTime() - new Date(a.edited_at ?? new Date(0)).getTime(),
      );
      setVisibleTestimonials(sortedTestimonials);
    }
  }, [testimonials]);

  const handleEdit = (id: number) => {
    setSearchParams({ edit: String(id) });
  };

  const handleDeleteClick = (id: number, name: string) => {
    setSelectedTestimonial({ id, name });
    setIsModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedTestimonial) {
      await removeTestimonial(selectedTestimonial.id);
      setIsModalOpen(false);
      setSelectedTestimonial(null);
    }
  };

  return (
    <div className="flex w-full flex-col h-[550px] overflow-hidden">
      <div className="overflow-y-auto pr-2">
        {fetchLoading ? (
          <section className="grid grid-cols-[repeat(auto-fill,_minmax(300px,_1fr))] gap-4 w-full">
            <Skeleton className="h-28" />
            <Skeleton className="h-28" />
            <Skeleton className="h-28" />
            <Skeleton className="h-28" />
            <Skeleton className="h-28" />
          </section>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,_minmax(300px,_1fr))] gap-4 w-full">
            {testimonials.length > 0 ? (
              visibleTestimonials.map((testimonial) => (
                <TestimoniCard
                  key={testimonial.testimoni_id}
                  testimoni_id={testimonial.testimoni_id}
                  name_testi={testimonial.name_testi}
                  message_testi_id={testimonial.message_testi_id}
                  photo_testi={testimonial.photo_testi || '/assets/no-data-image.svg'}
                  date={testimonial.created_at}
                  onDelete={() => handleDeleteClick(testimonial.testimoni_id, testimonial.name_testi)}
                  onEdit={() => handleEdit(testimonial.testimoni_id)}
                />
              ))
            ) : (
              <div className="w-full">
                <p className="px-3 py-2 text-center text-neutral-300">No data</p>
              </div>
            )}
          </div>
        )}
      </div>
      <ConfirmDeleteModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirmDelete}
        itemName={selectedTestimonial?.name}
        loading={removeLoading}
      />
    </div>
  );
}
